// ColumnsMenu — table column show/hide control. A preset over
// MultiSectionCheckboxDropdown (one "columns" section, columns-3 trigger, plain
// + portal so it isn't clipped by an overflow panel). Pairs with the
// useColumnVisibility hook, whose `menuProps` spread straight in.

import { useTranslation } from 'react-i18next';
import MultiSectionCheckboxDropdown from './MultiSectionCheckboxDropdown';
import type { DataTableColumn } from './DataTable';

export interface ColumnsMenuProps<T> {
  columns: DataTableColumn<T>[];
  /** Set of currently-visible column keys. */
  visible: Set<string>;
  onToggle: (key: string) => void;
  /** Keys shown disabled (locked visible). */
  lockedKeys?: string[];
  /** Keys left out of the menu entirely (always visible, never listed). */
  excludeKeys?: string[];
}

const ColumnsMenu = <T,>({
  columns,
  visible,
  onToggle,
  lockedKeys = [],
  excludeKeys = [],
}: ColumnsMenuProps<T>) => {
  const { t } = useTranslation();
  return (
    <MultiSectionCheckboxDropdown
      icon='columns-3'
      label={t('table_columns')}
      align='end'
      width={220}
      portal
      plainTrigger
      sections={[
        {
          key: 'columns',
          label: t('table_columns_menu_title'),
          options: columns
            .filter((c) => !excludeKeys.includes(c.key))
            .map((c) => ({
              id: c.key,
              label: String(c.label),
              disabled: lockedKeys.includes(c.key),
            })),
        },
      ]}
      selected={{ columns: visible }}
      onToggle={(_section, id) => onToggle(id)}
    />
  );
};

export default ColumnsMenu;
